import { useEffect, useCallback, useRef } from 'react';
import type { PitchClass, Accidental, NoteInput } from '../types/music';

/**
 * Valid pitch class keys (both upper and lower case)
 */
const VALID_PITCH_KEYS = new Set(['a', 'b', 'c', 'd', 'e', 'f', 'g', 'A', 'B', 'C', 'D', 'E', 'F', 'G']);

/**
 * Map key to pitch class
 */
function keyToPitchClass(key: string): PitchClass | null {
  const upper = key.toUpperCase();
  if (VALID_PITCH_KEYS.has(upper)) {
    return upper as PitchClass;
  }
  return null;
}

interface UseKeyboardInputOptions {
  /** Whether keyboard input is enabled */
  enabled: boolean;
  /** Callback when a valid note input is detected */
  onNoteInput: (input: NoteInput) => void;
  /** Optional callback for any key press (for debugging) */
  onKeyPress?: (key: string) => void;
}

interface UseKeyboardInputReturn {
  /** Currently pressed shift state */
  shiftState: 'none' | 'left' | 'right';
}

/**
 * Hook for handling keyboard input for note entry
 *
 * Listens for A-G keys and tracks shift key state to determine accidentals.
 * Left Shift + note = flat
 * Right Shift + note = sharp
 * No Shift = natural
 */
export function useKeyboardInput({
  enabled,
  onNoteInput,
  onKeyPress
}: UseKeyboardInputOptions): UseKeyboardInputReturn {
  // Track which shift key is currently pressed
  const shiftStateRef = useRef<'none' | 'left' | 'right'>('none');

  // Handle keydown events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (!enabled) return;

    // Track shift key presses
    if (event.key === 'Shift') {
      if (event.location === KeyboardEvent.DOM_KEY_LOCATION_LEFT) {
        shiftStateRef.current = 'left';
      } else if (event.location === KeyboardEvent.DOM_KEY_LOCATION_RIGHT) {
        shiftStateRef.current = 'right';
      }
      return;
    }

    // Check if it's a valid pitch class key
    const pitchClass = keyToPitchClass(event.key);
    if (!pitchClass) return;

    // Don't interfere with browser shortcuts
    if (event.ctrlKey || event.metaKey || event.altKey) return;

    // Prevent default to avoid typing in any inputs
    // Only if we're handling the key
    event.preventDefault();

    // Determine accidental based on current shift state
    let accidental: Accidental = 'natural';
    if (shiftStateRef.current === 'left') {
      accidental = 'flat';
    } else if (shiftStateRef.current === 'right') {
      accidental = 'sharp';
    }

    // Notify callback
    onNoteInput({ pitchClass, accidental });
    onKeyPress?.(event.key);
  }, [enabled, onNoteInput, onKeyPress]);

  // Handle keyup events (to track shift release)
  const handleKeyUp = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Shift') {
      shiftStateRef.current = 'none';
    }
  }, []);

  // Handle blur (reset shift state when window loses focus)
  const handleBlur = useCallback(() => {
    shiftStateRef.current = 'none';
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (!enabled) return;

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('blur', handleBlur);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('blur', handleBlur);
    };
  }, [enabled, handleKeyDown, handleKeyUp, handleBlur]);

  return {
    shiftState: shiftStateRef.current
  };
}

/**
 * Get display text for shift state
 */
export function getShiftStateDisplay(state: 'none' | 'left' | 'right'): string {
  switch (state) {
    case 'left': return 'Flat (♭)';
    case 'right': return 'Sharp (♯)';
    default: return 'Natural';
  }
}
